import { useState, useEffect } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Link,
  TextField,
  Typography,
  Divider,
} from '@mui/material'
import { useHistory } from 'react-router-dom'
import { Fingerprint } from '@mui/icons-material'

import { Props } from '../../Login.props'
import { ROUTES_PATHS } from 'core/routes'
import { MFAFactorType } from 'enums/multiFactorAuthentication'
import { t } from 'i18n'

import { useAutoFillCredentials } from 'utils/hooks/useAutoFillCredentials'
import { useIsMobile } from 'utils/hooks/useDeviceDetection'

const alternateMFAOptions = [
  {
    id: MFAFactorType.AuthApp,
    label: t('login.loginForm.labels.authApp'),
  },
  {
    id: MFAFactorType.Email,
    label: t('email'),
  },
  {
    id: MFAFactorType.SMS,
    label: t('phoneNumber'),
  },
  {
    id: MFAFactorType.BackupCode,
    label: t('login.loginForm.labels.useBackupCode'),
  },
]

export default function LoginForm(props: Props) {
  const {
    loginState,
    setLoginState,
    mfaDetails,
    loginMutation,
    startMFAAuthenticationMutation,
    finishMFAAuthenticationMutation,
    isBiometricAvailable,
    hasBiometricToken,
    onBiometricLogin,
    biometricLoading,
  } = props

  const history = useHistory()
  const isMobile = useIsMobile()

  const [selectedVerificationMethod, setSelectedVerificationMethod] = useState(
    mfaDetails?.factor_type,
  )

  const [showVerificationMethods, setShowVerificationMethods] = useState(false)

  const showBackupCodeInput = selectedVerificationMethod === MFAFactorType.BackupCode

  useAutoFillCredentials(setLoginState)

  useEffect(() => {
    setSelectedVerificationMethod(mfaDetails?.factor_type)
  }, [mfaDetails?.factor_type])

  const handleLogin = () => {
    if (mfaDetails) {
      finishMFAAuthenticationMutation.mutate(loginState)
    } else {
      const loginPayload = isMobile
        ? { ...loginState, remember_me: true }
        : loginState

      loginMutation.mutate(loginPayload)
    }
  }

  return (
    <Grid
      container
      onKeyUp={({ key }) =>
        key === 'Enter' &&
        (mfaDetails
          ? finishMFAAuthenticationMutation.mutate(loginState)
          : handleLogin())
      }
    >
      <Grid container justifyContent='center'>
        <Grid item xs={12}>
          <Typography variant='h5' color='text.secondary' align='center'>
            {t('login.loginForm.welcome')}
          </Typography>
        </Grid>
      </Grid>

      {!mfaDetails && isBiometricAvailable && hasBiometricToken && isMobile && (
        <Box textAlign="center" width="100%" mt={2} mb={3}>
          <Button
            variant="outlined"
            color="primary"
            fullWidth
            size="large"
            startIcon={
              biometricLoading ? (
                <CircularProgress size={20} />
              ) : (
                <Fingerprint />
              )
            }
            onClick={onBiometricLogin}
            disabled={biometricLoading || loginMutation.isLoading}
            sx={{
              py: 1.5,
              mb: 2,
              borderWidth: 2,
              '&:hover': {
                borderWidth: 2,
              },
            }}
          >
            {biometricLoading ? 'Authenticating...' : 'Enter with Biometrics'}
          </Button>

          <Divider sx={{ my: 2 }}>
            <Typography variant="body2" color="textSecondary">
              or
            </Typography>
          </Divider>
        </Box>
      )}

      <Box my={3} width={1}>
        <Grid container spacing={2}>
          {!mfaDetails && (
            <>
              <Grid item xs={12}>
                <TextField
                  id='email'
                  type='email'
                  label={t('email')}
                  variant='filled'
                  data-cy='email'
                  value={loginState.email}
                  onChange={e =>
                    setLoginState({
                      ...loginState,
                      email: e.target.value,
                    })
                  }
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  id='password'
                  type='password'
                  label={t('login.loginForm.labels.password')}
                  variant='filled'
                  data-cy='password'
                  value={loginState.password}
                  onChange={e =>
                    setLoginState({
                      ...loginState,
                      password: e.target.value,
                    })
                  }
                  fullWidth
                />
              </Grid>
            </>
          )}

          {mfaDetails && (
            <>
              <MFAVerificationCodeMessage
                mfaDetails={mfaDetails}
                selectedVerificationMethod={selectedVerificationMethod}
              />

              <Grid item xs={12}>
                <TextField
                  id='factor_code'
                  type='factor_code'
                  label={
                    showBackupCodeInput
                      ? t('login.loginForm.labels.backupCode')
                      : t('login.loginForm.labels.verificationCode')
                  }
                  variant='filled'
                  data-cy='factor_code'
                  value={loginState.mfa_backup_code}
                  onChange={e =>
                    setLoginState({
                      ...loginState,
                      factor_code: e.target.value,
                    })
                  }
                  fullWidth
                />
              </Grid>
            </>
          )}

          {mfaDetails?.mfa_type_options?.length > 0 && !showVerificationMethods && (
            <Grid item xs={12}>
              <Typography align='center'>
                <Link
                  onClick={() => setShowVerificationMethods(true)}
                  sx={{ cursor: 'pointer' }}
                >
                  {t('login.loginForm.labels.useDifferentMFAMethod')}
                </Link>
              </Typography>
            </Grid>
          )}

          {showVerificationMethods && (
            <Grid item xs={12}>
              {alternateMFAOptions
                .filter(
                  option =>
                    (option.id !== selectedVerificationMethod &&
                      mfaDetails?.mfa_type_options?.includes(option.id)) ||
                    option.id === MFAFactorType.BackupCode,
                )
                .map(option => (
                  <Grid
                    key={option.id}
                    container
                    spacing={2}
                    justifyContent='space-between'
                    alignItems='center'
                    sx={{ mb: 1 }}
                  >
                    <Grid item xs>
                      {option.label}
                    </Grid>

                    <Grid item xs='auto'>
                      <Button
                        color='primary'
                        variant='outlined'
                        size='small'
                        disabled={
                          loginState.factor_type === option.id &&
                          startMFAAuthenticationMutation.isLoading
                        }
                        onClick={() => {
                          setLoginState({
                            ...loginState,
                            factor_type: option.id,
                          })

                          startMFAAuthenticationMutation.mutate({
                            ...loginState,
                            factor_type: option.id,
                          })
                        }}
                      >
                        {loginState.factor_type === option.id &&
                          startMFAAuthenticationMutation.isLoading ? (
                          <CircularProgress size={24} />
                        ) : (
                          'Send'
                        )}
                      </Button>
                    </Grid>
                  </Grid>
                ))}
            </Grid>
          )}
        </Grid>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Button
            color='primary'
            variant='contained'
            size='large'
            onClick={handleLogin}
            disabled={
              loginMutation.isLoading ||
              finishMFAAuthenticationMutation.isLoading
            }
            data-cy='submit_form'
            fullWidth
          >
            {loginMutation.isLoading || finishMFAAuthenticationMutation.isLoading ? (
              <CircularProgress size={24} />
            ) : (
              t('login.loginForm.loginButton')
            )}
          </Button>
        </Grid>

        {!mfaDetails && (
          <Grid item xs={12}>
            <Button
              color='primary'
              variant='outlined'
              data-cy='forgot_password'
              size='large'
              onClick={() => history.push(ROUTES_PATHS.recoverPassword)}
              fullWidth
            >
              {t('login.loginForm.forgotPasswordButton')}
            </Button>
          </Grid>
        )}

        <Grid item xs={12} sx={{ textAlign: 'center' }}>
          <Link
            onClick={() => {
              localStorage.clear()
              window.location.reload()
            }}
            sx={{ fontSize: '0.85em', cursor: 'pointer' }}
          >
            Login problems? Click here to clear all cache and reload
          </Link>
        </Grid>
      </Grid>
    </Grid>
  )
}

function MFAVerificationCodeMessage({
  mfaDetails,
  selectedVerificationMethod,
}) {
  return (
    <Grid item>
      <Typography sx={{ mb: 2 }}>
        {selectedVerificationMethod === MFAFactorType.AuthApp &&
          t('mfa.enterVerificationCodeMessage.authenticatorApp')}
        {selectedVerificationMethod === MFAFactorType.SMS &&
          t('mfa.enterVerificationCodeMessage.sms', {
            phoneNumberLastDigits: mfaDetails.masked_phone_number,
          })}
        {selectedVerificationMethod === MFAFactorType.Email &&
          t('mfa.enterVerificationCodeMessage.email', {
            email: mfaDetails.masked_email,
          })}
      </Typography>
    </Grid>
  )
}
